import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { useSessionOrgUUID } from '#pplx/frontend/apps/ask/organization/hooks/useSessionOrgUUID.js';
import { useUserOrgData } from '#pplx/frontend/apps/ask/organization/hooks/useUserOrgData.js';
import { getEffectivePermissionsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/rbacKeys.js';
import { getStoredRevisionToken } from '#pplx/frontend/apps/ask/rbac/hooks/revision-token/revisionToken.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { enterpriseRolesClient } from '#pplx_generated/typespec/rest/enterprise_roles/client.js';
const EMPTY_PERMISSIONS = new Set();
const fetchEffectivePermissions = async (orgUuid, userId, reason, revisionToken) => {
    const { data, error, response } = await enterpriseRolesClient.GET('/rest/organizations/{organization_id}/permissions/{user_id}/effective', reason, {
        params: {
            path: { organization_id: orgUuid, user_id: userId },
            query: revisionToken ? { revision_token: revisionToken } : undefined,
        },
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            cause: error,
            status: response.status ?? 0,
            details: { reason, orgUuid, userId },
        });
    }
    return {
        permissions: new Set(data.permissions),
        isOrgAdmin: data.org_admin_membership,
        organizationCredentialVaultEnabled: data.organization_credential_vault_enabled,
        organizationCredentialVaultRoleAuthorized: data.organization_credential_vault_role_authorized,
    };
};
const EFFECTIVE_PERMISSIONS_STALE_TIME_MS = 60_000;
/**
 * Resolve the authenticated user's effective permission set on the current
 * session org.
 *
 * Fetches `GET /rest/organizations/{org}/permissions/{user}/effective` — the
 * BE iterates the catalog and runs `permissions_library.check()` for each id;
 * the response is the granted subset. Callers gate FE page renders and admin
 * sections via `permissions.has(id)`.
 *
 * The hook is read-only and cached per org/user via TanStack Query. Two
 * freshness escapes from the app-wide `staleTime: Infinity` default:
 *
 * - After the user's own role mutations, `refreshRbacCachesAfterMutation`
 *   invalidates this query. The revision token is read at fetch time (not
 *   baked into the query key), so the refetch carries read-after-write
 *   consistency while the previous set stays rendered — no gate flap.
 * - A finite `staleTime` lets the default refetch-on-focus/mount pick up
 *   grants changed by *other* admins within a minute of returning to the
 *   page, instead of requiring a full reload.
 */
export function useEffectivePermissions({ reason, enabled = true, }) {
    const { uuid: orgUuid } = useSessionOrgUUID();
    const { status } = useSession();
    const { orgUser, isLoading: isOrgUserLoading } = useUserOrgData({
        enabled,
        reason,
    });
    const orgUserUuid = orgUser?.uuid;
    const { data, isLoading, isError, isSuccess } = useQuery({
        queryKey: getEffectivePermissionsQueryKey(orgUuid ?? '', orgUserUuid ?? ''),
        queryFn: async () => {
            if (!orgUuid) {
                throw new Error('orgUuid required');
            }
            if (!orgUserUuid) {
                throw new Error('orgUserUuid required');
            }
            const revisionToken = getStoredRevisionToken(orgUuid);
            return fetchEffectivePermissions(orgUuid, orgUserUuid, reason, revisionToken);
        },
        enabled: enabled && !!orgUuid && !!orgUserUuid,
        staleTime: EFFECTIVE_PERMISSIONS_STALE_TIME_MS,
        meta: { skipPersistence: true },
    });
    const hasUsablePolicy = enabled && !isError;
    return {
        // Disabled ⇒ no grants, even when a previously-enabled fetch left data in
        // the React Query cache: "disabled" callers must never read stale grants.
        permissions: hasUsablePolicy ? (data?.permissions ?? EMPTY_PERMISSIONS) : EMPTY_PERMISSIONS,
        isOrgAdmin: hasUsablePolicy ? data?.isOrgAdmin : undefined,
        organizationCredentialVaultEnabled: hasUsablePolicy
            ? data?.organizationCredentialVaultEnabled
            : undefined,
        organizationCredentialVaultRoleAuthorized: hasUsablePolicy
            ? data?.organizationCredentialVaultRoleAuthorized
            : undefined,
        isLoading: isLoading || isOrgUserLoading || (enabled && status === 'loading'),
        isError,
        isLoaded: isSuccess,
    };
}
//# sourceMappingURL=useEffectivePermissions.js.map